import { FC, useMemo } from 'react';
import scheduleIcon from 'images/schedule-icon.svg';
import AptiveModal from 'components/modals/AptiveModal';
import { DatetimeHelper } from 'helpers/datetime';
import { WEEKDAY_FORMAT } from 'constants/datetime';
import { Appointment, CustomerSpot } from 'types/request';
import { useRecoilState } from 'recoil';
import { isAlreadyShowNotePopup } from 'app-recoil/atoms/issue-note';
import { appointmentTime } from 'utils/appointment';
import { spotTime } from 'utils/spot';

type Props = {
  upcomingAppointment: Appointment | undefined;
  isOpen: boolean;
  isLoading: boolean;
  isReschedule: boolean;
  selectedAvailableSpot: CustomerSpot | undefined;
  setOpen: (value: boolean) => void;
  onConfirm: () => void;
};

const ScheduleAppointmentDialog: FC<Props> = ({
  upcomingAppointment,
  isOpen,
  isLoading,
  isReschedule,
  selectedAvailableSpot,
  onConfirm,
  setOpen,
}) => {
  const [, setIsDoneNotePopup] = useRecoilState(isAlreadyShowNotePopup);

  const modalDescription = useMemo(
    () => (
      <p className="text-sm text-gray-500">
        {selectedAvailableSpot && (isReschedule ? (upcomingAppointment &&
          <>
            This will change your service appointment from
            <br />
            {DatetimeHelper.format(upcomingAppointment.start, WEEKDAY_FORMAT)}
            {' ' + appointmentTime(upcomingAppointment)}
            <br />
            to
            <br />
            {DatetimeHelper.format(selectedAvailableSpot.attributes.date, WEEKDAY_FORMAT)}
            {' ' + spotTime(selectedAvailableSpot)}
            <br />
          </>
        ) : (
          <>
            This will schedule a service appointment for
            <br />
            {DatetimeHelper.format(selectedAvailableSpot.attributes.date, WEEKDAY_FORMAT)}
            {' ' + spotTime(selectedAvailableSpot)}
          </>
        ))}
      </p>
    ),
    [isReschedule, upcomingAppointment, selectedAvailableSpot]
  );

  return (
    <AptiveModal
      icon={scheduleIcon}
      title={isReschedule ? 'Confirm Reschedule' : 'Confirm Appointment'}
      description={modalDescription}
      approveButtonText="Confirm"
      approveButtonColor="primary"
      isOpen={isOpen}
      setOpen={setOpen}
      isLoading={isLoading}
      confirmCallback={() => {
        onConfirm && onConfirm();
        setIsDoneNotePopup(false);
      }}
      trackingInfo={{ name: 'popup_confirm', buttonApproval: 'click_confirm', buttonCancel: 'click_cancel' }}
      cancelCallback={() => {
        setOpen(false);
      }}
    />
  );
};

export default ScheduleAppointmentDialog;
